import React, {useMemo} from 'react';
import {Menu} from 'antd';
import {NavLink, useLocation} from 'react-router-dom';
import {
  AppstoreOutlined,
    BookOutlined,
    FileTextOutlined,
    FolderOutlined,
    LockOutlined,
  MessageOutlined,
  SettingOutlined
} from '@ant-design/icons';
import {useMenuContext} from '../contexts/MenuContext';
import './MainMenu.css';

function MainMenu() {
  const { menus } = useMenuContext() || { menus: [] };
  const location = useLocation();

  const dynamicRoots = useMemo(() => {
    const list = (menus || []).filter(m => m && m.useYn !== 'N');
    // dummy root 정의: parentMenuId == null && path == null && rteKey == null
    const dummyRoots = list.filter(m => !m.parentMenuId && !m.path && !m.rteKey);
    if (dummyRoots.length === 0) {
      return list.filter(m => !m.parentMenuId); // 기존 방식
    }
    const dummyIds = new Set(dummyRoots.map(d => d.menuId));
    // 승격 조건: (1) 원래 루트 + path/rteKey 있는 것, (2) dummy root 의 직계 자식
    return list.filter(m => (
      (!m.parentMenuId && (m.path || m.rteKey)) ||
      (m.parentMenuId && dummyIds.has(m.parentMenuId))
    ));
  }, [menus]);

  // 아이콘 매핑 (간단)
  const iconMap = {
    PROJECT_LIST: <FolderOutlined />,
    TERM_LIST: <BookOutlined />,
    WORD_LIST: <FileTextOutlined />,
    DOMAIN_LIST: <AppstoreOutlined />,
    MENU_MANAGE: <SettingOutlined />,
    SYSTEM_SETTINGS: <SettingOutlined />,
    BOARD: <MessageOutlined />,
    BOARD_HOME: <MessageOutlined />,
    BOARD_LIST: <MessageOutlined />
  };

  const toMenuItem = (m) => {
    const disabled = !!m.disabled; // 백엔드/MeService 계산된 disabled 사용
    const icon = iconMap[m.rteKey] || (disabled ? <LockOutlined /> : <FolderOutlined />);
    const isNavigable = !disabled && m.path; // path 있을 때만 NavLink
    const label = isNavigable ? (
      <NavLink to={m.path}>{m.menuNm}</NavLink>
    ) : (
      <span style={{ color: disabled ? '#999' : 'inherit', cursor: disabled ? 'not-allowed' : 'default' }}>{m.menuNm}</span>
    );
    return {
      key: m.path || m.menuId,
      icon,
      label,
      disabled
    };
  };

  const items = dynamicRoots.length ? dynamicRoots
    .sort((a,b)=> (a.sortSeq ?? 0) - (b.sortSeq ?? 0))
    .map(toMenuItem) : [
      { key: '/terms', icon: <BookOutlined />, label: <NavLink to="/terms">용어</NavLink> },
      { key: '/words', icon: <FileTextOutlined />, label: <NavLink to="/words">단어</NavLink> },
      { key: '/domains', icon: <AppstoreOutlined />, label: <NavLink to="/domains">도메인</NavLink> },
      { key: '/projects', icon: <FolderOutlined />, label: <NavLink to="/projects">프로젝트</NavLink> },
      { key: '/board', icon: <MessageOutlined />, label: <NavLink to="/board">게시판</NavLink> },
      { key: '/system/menus', icon: <SettingOutlined />, label: <NavLink to="/system/menus">시스템설정</NavLink> },
    ];

  // 현재 경로와 가장 잘 맞는 key 선택
  const selectedKey = items.find(it => typeof it.key === 'string' && location.pathname.startsWith(it.key))?.key || location.pathname;

  return (
    <div className="main-menu-wrapper main-menu-with-disabled">
      <Menu
        mode="horizontal"
        selectedKeys={[selectedKey]}
        items={items}
      />
    </div>
  );
}

export default MainMenu;
